import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AccountService } from 'src/app/pages/account/services/account.service';
import { environment } from 'src/environments/environment';

@Injectable({
	providedIn: 'root',
})
export class AnalyticsService {
	adminApi: string = environment.ADMIN_API;
	constructor(
		private accountService: AccountService,
		private http: HttpClient
	) {}

	/**
	 * Fires an analytics event using the Zaraz tracking library.
	 * @param eventName - The name of the event to track.
	 * @param data - Additional data associated with the event (optional).
	 */
	async fireAnalyticsEvent(eventName: string, data?: any): Promise<void> {
		try {
			// Get the Zaraz tracking library from the window object
			const zaraz = (window as any)?.zaraz;

			// Clone the original data to avoid mutating it
			const eventData = { ...(data || {}) };

			if (eventData.email) {
				eventData.email = await this.hashSHA256(eventData.email);
			}
			if (eventData.phone) {
				eventData.phone = await this.hashSHA256(eventData.phone);
			}
			if (eventData.profile_name) {
				eventData.profile_name = await this.hashSHA256(eventData.profile_name);
			}
			if (eventData.first_name) {
				eventData.first_name = await this.hashSHA256(eventData.first_name);
			}
			if (eventData.last_name) {
				eventData.last_name = await this.hashSHA256(eventData.last_name);
			}

			// Track the event using Zaraz
			zaraz?.track(eventName, eventData);
		} catch (error) {
			console.error(
				'Error while tracking analytics event:',
				'Event: ' + eventName + ' | Error: ' + error
			);
		}
	}

	// SHA-256 hashing helper
	private async hashSHA256(value: string): Promise<string> {
		const encoder = new TextEncoder();
		const data = encoder.encode(value.trim().toLowerCase());
		const hashBuffer = await crypto.subtle.digest('SHA-256', data);
		return Array.from(new Uint8Array(hashBuffer))
			.map((b) => b.toString(16).padStart(2, '0'))
			.join('');
	}

	/**
	 * Opens the cookie settings modal using the Zaraz tracking library.
	 */
	openCookieSettings() {
		try {
			// Get the Zaraz tracking library from the window object
			const zaraz = (window as any)?.zaraz;
			let locale = this.getLangLocale('WD_locale');
			zaraz?.set('__zarazConsentLanguage', locale);
			// Show the consent modal using Zaraz
			zaraz?.showConsentModal();
		} catch (error) {
			// Log an error message if opening the cookie settings modal fails
			console.error('Error while opening cookie settings modal:', error);
		}
	}

	/**
	 * Opens the cookie settings modal using the Zaraz tracking library.
	 */
	setZarazLanguage() {
		try {
			// Get the Zaraz tracking library from the window object
			const zaraz = (window as any)?.zaraz;
			let locale = this.getLangLocale('WD_locale');
			// Show the consent modal using Zaraz
			zaraz?.set('__zarazConsentLanguage', locale);
		} catch (error) {
			// Log an error message if opening the cookie settings modal fails
			console.error('Error while opening cookie settings modal:', error);
		}
	}

	// Helper function to get a locale value by key from localStorage
	getLangLocale(key: string): string {
		try {
			const locale = localStorage.getItem(key);
			return locale ? locale : 'nl'; // Default to 'nl' if not found
		} catch (error) {
			console.error('Error accessing localStorage:', error);
			return 'nl'; // Default to 'nl' in case of an error
		}
	}

	sendConversion(profileId: string): Observable<any> {
		const url = `${this.adminApi}/conversion/${profileId}`;
		return this.http.get(url);
	}
}
